import { useMemberSubscription, useQueryRulesAsPublic } from "@gymflow/api";
import { DurationType } from "@gymflow/common";
import {
  DATE_ONLY_FORMAT,
  iso8601WithoutTz,
  pluralize,
  utcToZonedTime,
} from "@gymflow/helpers";
import {
  Button,
  ClockIcon,
  CloseIcon,
  FormLabeledSwitch,
  InfoBlock,
  WarningBlock,
} from "apps/portal/src/components/atoms";
import { FormSingleDatePicker } from "apps/portal/src/components/atoms/form/FormSingleDatePicker";
import { CancelIcon } from "apps/portal/src/components/atoms/icons/CancelIcon";
import { FileShield02Icon } from "apps/portal/src/components/atoms/icons/FileShield02Icon";
import { generateDates, getDateTimeInGym } from "apps/portal/src/helpers/date";
import { useClubSettings } from "apps/portal/src/providers";
import useGymflowModels from "apps/portal/src/store";
import { parse } from "date-fns";
import { FormikProvider, useFormik } from "formik";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { DatePickerDayRedDot } from "../../../../molecules/DatePicker/components/DatePickerDayRedDot";
import { FormCancellationReasonSelect } from "./components/FormCancellationReasonSelect";
import {
  cancelMembershipFormSchema,
  INITIAL_VALUES_CANCEL_MEMBERSHIP_FORM,
} from "./constants";
import { CancelMembershipFormProps, CancelMembershipFormValues } from "./types";

export const CancelMembershipForm = ({
  onClose,
  membership,
  memberId,
  linkedMembers,
}: CancelMembershipFormProps) => {
  const { t } = useTranslation();
  const { club } = useClubSettings();
  const { api } = useGymflowModels();

  const { cancelSubscriptionMutation } = useMemberSubscription({
    api,
    tz: club.timezone,
  });

  const { data: rules } = useQueryRulesAsPublic({
    api,
    params: membership.membershipBean.id
      ? {
          membershipId: membership.membershipBean.id,
        }
      : undefined,
  });

  const minimumPeriodNotice = useMemo(() => {
    if (!rules) {
      return 0;
    }
    return (
      rules.ruleClubList.find(
        (rule) => rule.ruleType === "SUBSCRIPTION_CANCELLATION",
      )?.subscriptionCancellationRule?.minimumPeriodNotice ?? 0
    );
  }, [rules]);

  const formik = useFormik<CancelMembershipFormValues>({
    initialValues: INITIAL_VALUES_CANCEL_MEMBERSHIP_FORM,
    validationSchema: toFormikValidationSchema(cancelMembershipFormSchema),
    onSubmit: async ({ isChangeImmediately, cancellationReason, date }) => {
      if (!cancellationReason) {
        return;
      }

      await cancelSubscriptionMutation.mutateAsync({
        subscriptionId: membership.id,
        userMemberId: memberId,
        cancellationReasonId: cancellationReason.value,
        effectiveImmediately: isChangeImmediately ? true : undefined,
        cancellationDate:
          !isChangeImmediately && date
            ? (DateTime.fromJSDate(date)
                .endOf("day")
                .setZone(club.timezone)
                .endOf("day")
                .toUTC()
                .set({ millisecond: 0 })
                .toISO({ suppressMilliseconds: true }) ?? "")
            : undefined,
      });
      onClose();
    },
  });

  const { isChangeImmediately } = formik.values;

  // Copy-past from previous variant, refactor it
  const cancellingDates = useMemo(
    () =>
      generateDates({
        intervalType: (DurationType as any).format(
          membership.billingType,
          true,
        ),
        intervalValue: membership.billingPeriod ?? 0,
        start: parse(
          utcToZonedTime(membership.endDate, club.timezone),
          iso8601WithoutTz,
          new Date(),
        ),
        dateFormat: DATE_ONLY_FORMAT,
      }).map((e) => DateTime.fromISO(e).plus({ days: 1 })),
    [
      club.timezone,
      membership.billingPeriod,
      membership.billingType,
      membership.endDate,
    ],
  );

  const disabledDate = useMemo(() => {
    const nowInGym = getDateTimeInGym(club.timezone);

    return {
      before: nowInGym.add(1, "day").toDate(),
    };
  }, [club.timezone]);

  const onCloseSidebar = useCallback(() => {
    onClose();
    formik.resetForm();
  }, [formik, onClose]);

  return (
    <FormikProvider value={formik}>
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col justify-between gap-4 border-b border-gray-300 p-6 pb-5">
            <div className="flex flex-row items-start justify-between">
              <div className="border-error-50 bg-error-100 flex items-center rounded-full border-8 p-1.5">
                <CancelIcon className="size-6" />
              </div>
              <div className="cursor-pointer" onClick={() => onCloseSidebar()}>
                <CloseIcon className="size-6" />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-lg font-semibold text-gray-950">
                {t("components.cancelMembershipSidebar.title")}
              </div>
              <div className="text-sm font-medium text-gray-500">
                {t("components.cancelMembershipSidebar.description")}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-5 px-6 pt-4">
            {membership.id !== membership.primarySubscriptionId && (
              <WarningBlock
                text={t(
                  "components.cancelMembershipSidebar.warningBlock.partOfShared",
                )}
              />
            )}
            {membership.id === membership.primarySubscriptionId &&
              linkedMembers &&
              linkedMembers?.length > 1 && (
                <WarningBlock
                  text={t(
                    "components.cancelMembershipSidebar.warningBlock.removedForAllLinkedMembers",
                  )}
                />
              )}
            <div className="flex flex-col gap-3">
              {membership?.endContractDate && (
                <div className="flex items-center justify-between">
                  <div className="flex gap-1.5">
                    <FileShield02Icon
                      className="size-5"
                      pathClassName="stroke-gray-500 dark:stroke-gray-400"
                    />
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t("components.cancelMembershipSidebar.contractEndDate")}
                    </div>
                  </div>
                  <div className="dark:text-gray-25 text-base font-semibold text-gray-950">
                    {DateTime.fromISO(membership.endContractDate)
                      .setZone(club.timezone)
                      .toLocaleString(DateTime.DATE_MED)}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex gap-1.5">
                  <ClockIcon
                    className="size-5"
                    pathClassName="stroke-gray-500 dark:stroke-gray-400 stroke-1"
                  />
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t(
                      "components.cancelMembershipSidebar.cancellationNoticePeriod",
                    )}
                  </div>
                </div>
                <div className="dark:text-gray-25 text-base font-semibold text-gray-950">
                  {minimumPeriodNotice === 0
                    ? t("components.cancelMembershipSidebar.noNoticePeriod")
                    : pluralize(
                        t("components.cancelMembershipSidebar.month", {
                          minimumPeriodNotice,
                        }),
                        t("components.cancelMembershipSidebar.months", {
                          minimumPeriodNotice,
                        }),
                        minimumPeriodNotice,
                      )}
                </div>
              </div>
            </div>
            <div className="h-px w-full bg-gray-300" />
            <FormCancellationReasonSelect />
            <FormLabeledSwitch<CancelMembershipFormValues>
              name="isChangeImmediately"
              label={t("components.cancelMembershipSidebar.switch.label")}
            />
            {!isChangeImmediately && (
              <>
                <FormSingleDatePicker<CancelMembershipFormValues>
                  name="date"
                  disabled={disabledDate}
                  components={{
                    Day: ({ day: { date }, day, ...dayProps }) => (
                      <DatePickerDayRedDot
                        day={day}
                        {...dayProps}
                        showRedDot={cancellingDates.some((d) =>
                          d.hasSame(
                            DateTime.fromISO(date.toISOString()),
                            "day",
                          ),
                        )}
                      />
                    ),
                  }}
                  label={t("components.cancelMembershipSidebar.data.label")}
                />
                <InfoBlock
                  text={t("components.cancelMembershipSidebar.infoBlock")}
                  className="items-center"
                />
              </>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-t-gray-300 p-6">
          <Button
            intent="default"
            className="w-full"
            onClick={() => onCloseSidebar()}
          >
            {t("portal.common.cancel")}
          </Button>
          <Button
            intent="danger"
            className="w-full"
            onClick={async () => await formik.submitForm()}
          >
            {t("portal.common.confirm")}
          </Button>
        </div>
      </div>
    </FormikProvider>
  );
};
